<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  show: boolean;
  orderData?: any;
}>();

const emit = defineEmits(['close']);

const ticketArray = computed(() => {
    if (!props.orderData?.tickets) return [];
    return Array.isArray(props.orderData.tickets) ? props.orderData.tickets : [props.orderData.tickets];
});

const closeAndRedirect = () => {
    emit('close');
};

const saveTicket = () => {
    if (!props.orderData) return;
    
    // Safety destructure
    const data = JSON.parse(JSON.stringify(props.orderData));
    const { client, orderId, date, totalAmount, tickets } = data;
    
    // Formatting
    const fmtAmount = new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 }).format(totalAmount);
    const dateStr = new Date(date).toLocaleString('es-VE');
    const qrData = `ID:${client.cedula}|T:${client.phone}|DATE:${date}|SIG:${orderId.substring(0,8)}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`;
    
    const ticketListStr = Array.isArray(tickets) ? tickets.map((t:any) => String(t).padStart(4,'0')).join(', ') : tickets;

    const ticketHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Ticket - La Favorita</title>
        <style>
          @page { size: auto; margin: 0mm; }
          body { 
            font-family: 'Verdana', sans-serif; 
            padding: 40px; 
            max-width: 500px; 
            margin: 0 auto; 
            background: #fff; 
            color: #000;
          }
          .ticket-container {
             border: 2px solid #000;
             padding: 20px;
             position: relative;
          }
          .header { 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-start;
              border-bottom: 2px solid #000; 
              padding-bottom: 15px; 
              margin-bottom: 20px; 
          }
          .brand h1 { margin: 0; font-size: 24px; font-weight: 900; letter-spacing: -1px; }
          .brand p { margin: 0; font-size: 12px; text-transform: uppercase; }
          
          .qr-box { 
              text-align: right; 
          }
          
          .info-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; }
          .info-label { font-weight: bold; text-transform: uppercase; font-size: 10px; color: #555; }
          .info-value { font-weight: bold; }
          
          .ticket-grid {
              background: #f0f0f0;
              padding: 15px;
              margin: 20px 0;
              text-align: center;
              font-family: 'Courier New', monospace;
              font-weight: bold;
              font-size: 16px;
              word-break: break-all;
              line-height: 1.5;
          }
          
          .total-box {
              border-top: 2px dashed #000;
              padding-top: 15px;
              display: flex;
              justify-content: space-between;
              font-size: 20px;
              font-weight: 900;
              margin-bottom: 20px;
          }
          
          .footer {
              font-size: 10px;
              text-align: center;
              color: #666;
              margin-top: 30px;
          }
          
          .signature {
              margin-top: 10px;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              border: 1px solid #ccc;
              padding: 5px;
              display: inline-block;
          }
        </style>
      </head>
      <body>
        <div class="ticket-container">
            <div class="header">
                <div class="brand">
                    <h1>LA FAVORITA</h1>
                    <p>Comprobante de Reserva</p>
                    <p style="margin-top:5px; font-size:10px;">ID Orden: ${orderId.substring(0,8)}</p>
                </div>
                <div class="qr-box">
                    <img src="${qrUrl}" width="100" height="100" />
                </div>
            </div>
            
            <div class="info-row">
                <div>
                    <div class="info-label">Cliente</div>
                    <div class="info-value" style="text-transform:uppercase;">${client.full_name}</div>
                </div>
                <div style="text-align:right;">
                    <div class="info-label">Fecha</div>
                    <div class="info-value">${dateStr.split(',')[0]}</div>
                </div>
            </div>

            <div class="info-row">
                <div>
                    <div class="info-label">Documento ID</div>
                    <div class="info-value">${client.cedula}</div>
                </div>
                <div style="text-align:right;">
                    <div class="info-label">Teléfono</div>
                    <div class="info-value">${client.phone}</div>
                </div>
            </div>

            <div class="ticket-grid">
                <div class="info-label" style="margin-bottom:5px;">TUS NÚMEROS DE LA SUERTE</div>
                ${ticketListStr}
            </div>
            
            <div class="total-box">
                <span>TOTAL A PAGAR</span>
                <div style="text-align:right">
                    <div>${fmtAmount} Ref</div>
                    ${client.amount_bs ? `<div style="font-size:14px; font-weight:normal; color:#555;">(Bs. ${new Intl.NumberFormat('es-VE').format(client.amount_bs)})</div>` : ''}
                </div>
            </div>
            
            <div style="font-size:12px;">
                <div class="info-row">
                    <span class="info-label">Método de Pago</span>
                    <span>${client.payment_method}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Referencia</span>
                    <span>${client.payment_ref}</span>
                </div>
            </div>

            <div class="footer">
                <p>Este comprobante certifica la intención de compra.</p>
                <p>La validación final está sujeta a la confirmacióndel pago en 24h.</p>
                <div class="signature">FIRMA DIGITAL: ${orderId}</div>
            </div>
        </div>
        <script>
            window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } };
        <` + `/script>
      </body>
    </html>
    `;
    
    const win = window.open('', '_blank');
    if (win) {
        win.document.write(ticketHtml);
        win.document.close();
    } else {
        alert("Por favor permite las ventanas emergentes para descargar el ticket.");
    }
    
    // Redirect after initiate print (user intent is to finish)
    setTimeout(() => {
        closeAndRedirect();
    }, 1000);
};
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/90 backdrop-blur-xl animate-in fade-in duration-500"></div>

    <!-- Modal Content -->
    <div class="relative z-10 w-full max-w-sm flex flex-col items-center text-center animate-in zoom-in-90 duration-500">
      
      <!-- Icon -->
      <div class="mb-6 relative">
        <div class="absolute inset-0 bg-green-500 blur-[60px] animate-pulse rounded-full opacity-30"></div>
        <div class="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center border-2 border-green-500/50 text-green-500 text-5xl shadow-[0_0_30px_rgba(34,197,94,0.3)]">
            ✅
        </div>
      </div>

      <h2 class="text-3xl font-black text-white uppercase tracking-wider mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">Reserva Exitosa</h2>
      <div class="h-1 w-24 bg-green-500 rounded-full mb-6"></div>
      
      <p class="text-gray-300 text-sm mb-6 leading-relaxed bg-white/5 p-4 rounded-xl border border-white/10">
          Tu reserva ha sido registrada correctamente. <br/>
          Una vez verifiquemos tu pago (aprox <strong class="text-green-400">24h</strong>), recibirás tus boletos definitivos.
      </p>

      <div class="w-full bg-black/40 border border-white/10 rounded-xl p-4 mb-6">
          <p class="text-xs text-gray-400 uppercase font-bold mb-2">Tus Tickets Asignados</p>
          <div class="flex flex-wrap gap-2 justify-center max-h-32 overflow-y-auto custom-scrollbar">
              <span v-for="t in ticketArray" :key="t" class="bg-gold-500 text-black font-black font-mono px-2 py-1 rounded text-sm shadow">
                  {{ String(t).padStart(4, '0') }}
              </span>
          </div>
      </div>

      <div class="flex flex-col gap-3 w-full">
        <button v-if="orderData" @click="saveTicket" class="w-full py-3 bg-white hover:bg-gray-200 text-black font-bold uppercase tracking-widest rounded-xl transition-all shadow-lg flex items-center justify-center gap-2">
            <span>💾 Guardar Comprobante</span>
        </button>
        <button @click="closeAndRedirect" class="w-full py-4 bg-transparent hover:bg-white/5 text-gray-400 font-medium text-sm transition-all">
            Volver al Inicio
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

.animate-heartbeat {
  animation: heartbeat 1.5s ease-in-out infinite;
}
</style>
